import { COUNTRY_CODES, CURRENCIES } from '@savgroup-front-common/constants';

import { NotOpenForClaimsReason } from './Claim';
import { OrderDetailsProduct } from './OrderDetails';
import { OutcomeHistory, ProductHistory } from './ProductHistory';
import { ProductStates } from './ProductStates';
import { ModelDimension } from './SparePartCatalog';

export { PRODUCT_OUTCOME, ProductType } from './ProductHistory';
export type { OutcomeHistory, ProductHistory };

export type Product = OrderDetailsProduct;

export interface GroupedProduct {
  brandId: string;
  brandName: string;
  ean: string;
  externalIdentifier: string;
  modelId: string;
  modelName: string;
  modelDisplayName: string;
  modelTypeId: string;
  modelTypeName: string;
  notOpenForClaims: boolean;
  notOpenForClaimsReason?: NotOpenForClaimsReason;
  claimGroupId?: string;
  claimId?: string;
  orderLineReference: string;
  orderLineReferences: string[];
  ownerProductId: string;
  ownerProductIds: string[];
  price: number;
  priceCurrencyCode: CURRENCIES;
  shippingPrice: number;
  sku: string;
  soldWarrantyId?: string;
  soldWarrantyPrice?: number;
  state: ProductStates;
  supplierId?: string;
  productHistory?: ProductHistory[];
  isAvailableForClaims: boolean;
  orderLineType?: OrderLineType;
}

export interface ProductToReturn {
  quantity?: number;
  ownerProductId?: string | string[];
  unitPrice?: string;
  orderLineReference?: string;
  orderLineReferences?: string[];
  quantityToReturn?: number;

  model?: { value: string } | string;
  modelId?: string;
  modelName?: string;
  modelTypeId?: string;
  modelTypeName?: string;
  isMutable?: boolean;
  internalId?: string;
  modelDisplayName: string;
  modelTypeDisplayName: string;
  brandDisplayName: string;
}

export interface ProductInformation {
  brandId: string;
  brandName: string;
  ean: string;
  modelId: string;
  modelName: string;
  modelTypeId: string;
  modelTypeName: string;
  price: number;
  priceCurrencyCode: CURRENCIES;
  ownerProductId: string;
  shippingPrice: number;
  sku: string;
  soldWarrantyPrice: number;
  state: string;
  supplierId: string;
  productHistory?: ProductHistory[];
  quantity?: number;
  internalId?: string;
  weight: number;
  modelDimensions: ModelDimension;
  outcomeHistory?: OutcomeHistory[];
  isDisabled?: boolean;
  modelDisplayName: string;
  brandDisplayName: string;
}

export interface Adequacy {
  orderLineReference: string;
  adequacy: string;
  orientationId: string;
}

export interface ProductQualification {
  id: string;
  uniqueName: string;
  isAsExpected: boolean;
  isProductOutcomeExpected: boolean;
  isAvailableForUnexpectedProduct: boolean;
  orderDisplay: number;
  isFraudReport?: boolean;
  tenantId: string;
  sellerId: string;
  localeList?: {
    label: string;
    cultureCode: COUNTRY_CODES;
  }[];
}

export interface ProductOrientation {
  id: string;
  name: string;
  uniqueName: string;
  productOutcome: string;
  orderDisplay: number;
  isPresetDefaultValue: boolean;
}

export interface SoldServices {
  orderLineReference: string;
  name: string;
  sku: string;
  price: number;
  priceCurrencyCode: CURRENCIES;
  soldServiceType?: string;
}

export enum OrderDelivery {
  NOT_SET = 'NotSet',
  HOME_DELIVERY = 'HomeDelivery',
  SCHEDULED_DOOR_STEP_DELIVERY = 'ScheduledDoorstepDelivery',
  SCHEDULED_IN_HOME_DELIVERY = 'ScheduledInHomeDelivery',
  IN_STORE_PICKUP_DELIVERY = 'InStorePickUpDelivery',
  LOCAL_PICKUP_DELIVERY = 'LocalPickUpDelivery',
  CLICK_AND_COLLECT = 'ClickAndCollect',
  RESERVE_AND_COLLECT = 'ReserveAndCollect',
}

export enum ModelState {
  UNDEFINED = 'Undefined',
  NEW = 'New',
  USED = 'Used',
  RECONDITIONED = 'Reconditioned',
}

export enum SafetyRiskStatus {
  NONE = 'None',
  DECLARED = 'Declared',
  CONFIRMED = 'Confirmed',
  REJECTED = 'Rejected',
}

export interface OwnerProductSummary {
  ownerId: string;
  ownerProductId: string;
  sellerId: string;
  sellerBrand?: string;
  buyDate?: string;
  orderDeliveryDateUtc?: string;
  orderDeliveryMethod: OrderDelivery;
  marketPlace?: string;
  soldWarrantyId?: string;
  soldWarrantyPurchaseDate?: string;
  claimId?: string;
  claimGroupId?: string;
  fileId?: string;
  hasOpenFile: boolean;
  orderId?: string;
  orderReference?: string;
  orderLineReference?: string;
  externalIdentifier?: string;
  modelId: string;
  notOpenForClaims: boolean;
  isLowValue?: boolean;
  currencyCode?: string;
  price: number;
  shippingPrice: number;
  soldWarrantyPrice: number;
  state: ModelState;
  imei: string[];
  serialNumber?: string;
  outcomesHistory: OutcomeHistory[];
  countRepairs: number;
  deliveryTransportCarrier: {
    deliveryCarrierName?: string;
    deliveryCarrierCode?: string;
  };
  safetyRiskStatus: SafetyRiskStatus;
}

export const OrderLineType = {
  GIFT: 'Gift',
  SAMPLE: 'Sample',
  STANDARD: 'Standard',
} as const;

export type OrderLineType = (typeof OrderLineType)[keyof typeof OrderLineType];
